import React, { useEffect, useRef, useState } from 'react'
import { onAuthStateChanged, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import { ShieldCheck, Clock3, Camera, Bell } from 'lucide-react'
import { auth, getFns } from '../../firebase'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import TimesheetDashboard from './TimesheetDashboard'

const mergeRows = (oldRows, rows) => [...oldRows, ...rows.filter(row => !oldRows.some(old => old.id === row.id))]
const call = async (name, payload) => (await httpsCallable(getFns(), name)(payload)).data

export default function ReviewDashboardPanel() {
  const [signedIn, setSignedIn] = useState('')
  const [records, setRecords] = useState(null), [alerts, setAlerts] = useState(null)
  const [gallery, setGallery] = useState(null), [selectedEntryId, setSelectedEntryId] = useState(null)
  const [busy, setBusy] = useState(''), [error, setError] = useState('')
  const credentials = useRef(null), epoch = useRef(0), inFlight = useRef(false)
  const clear = () => {
    ++epoch.current; credentials.current = null; inFlight.current = false
    setRecords(null); setAlerts(null); setGallery(null); setSelectedEntryId(null); setBusy(''); setError('')
  }
  useEffect(() => {
    const off = onAuthStateChanged(auth, user => { clear(); setSignedIn(user?.uid || '') })
    return () => { off(); ++epoch.current; credentials.current = null }
  }, [])

  const failure = (err, message) => {
    if (['functions/permission-denied', 'functions/unauthenticated'].includes(err?.code)) {
      clear(); setError('Access could not be verified. Sign in with an account authorized to manage attendance.')
    } else setError(message)
  }
  const fetchPhotos = async (entry, proof, request, previous = null) => {
    const data = await call('timesheetReviewerPhotos', { ...proof, entryId: entry.id, ...(previous?.nextCursor ? { cursor: previous.nextCursor } : {}) })
    if (request !== epoch.current) return
    if (data?.entryId !== entry.id || !Array.isArray(data.photos)
      || data.photos.some(photo => typeof photo.dataUrl !== 'string' || !photo.dataUrl.startsWith('data:image/jpeg;base64,'))) throw new Error('Invalid photos')
    setGallery({ ...data, photos: previous ? mergeRows(previous.photos, data.photos) : data.photos })
  }
  const load = async () => {
    if (inFlight.current || !auth.currentUser) return
    const proof = {}
    inFlight.current = true
    const request = ++epoch.current
    setBusy('refresh'); setError('')
    try {
      const [nextRecords, nextAlerts] = await Promise.all([
        call('timesheetReviewerEntries', proof), call('timesheetReviewerList', proof),
      ])
      if (request !== epoch.current) return
      if (!Array.isArray(nextRecords?.entries) || !Array.isArray(nextAlerts?.notifications) || !nextAlerts.reviewer
        || !Number.isFinite(Date.parse(nextRecords.serverNow))) throw new Error('Invalid dashboard')
      credentials.current = proof; setRecords(nextRecords); setAlerts(nextAlerts); setGallery(null)
      setSelectedEntryId(null)
    } catch (err) { if (request === epoch.current) failure(err, 'Could not load the dashboard. Check your connection and try again.') }
    finally { if (request === epoch.current) { inFlight.current = false; setBusy('') } }
  }
  const select = async (entry, more = false) => {
    if (inFlight.current || !credentials.current || (more && !gallery?.nextCursor)) return
    inFlight.current = true
    const request = ++epoch.current
    setSelectedEntryId(entry.id); setBusy('photos'); setError('')
    if (!more) setGallery(null)
    try { await fetchPhotos(entry, credentials.current, request, more ? gallery : null) }
    catch (err) { if (request === epoch.current) failure(err, 'Could not load the photos for this shift. Select it again to retry.') }
    finally { if (request === epoch.current) { inFlight.current = false; setBusy('') } }
  }
  const more = async kind => {
    const current = kind === 'records' ? records : alerts
    if (inFlight.current || !credentials.current || !current?.nextCursor) return
    inFlight.current = true
    const request = ++epoch.current
    setBusy(kind); setError('')
    try {
      const data = await call(kind === 'records' ? 'timesheetReviewerEntries' : 'timesheetReviewerList', { ...credentials.current, cursor: current.nextCursor })
      if (request !== epoch.current) return
      const key = kind === 'records' ? 'entries' : 'notifications'
      if (!Array.isArray(data?.[key])) throw new Error('Invalid page')
      const next = { ...current, hasMore: data.hasMore, nextCursor: data.nextCursor, [key]: mergeRows(current[key], data[key]) }
      if (kind === 'records') setRecords(next); else setAlerts(next)
    } catch (err) { if (request === epoch.current) failure(err, 'Could not load older items. Please try again.') }
    finally { if (request === epoch.current) { inFlight.current = false; setBusy('') } }
  }
  const loadAll = async () => {
    if (inFlight.current || !credentials.current || !records?.hasMore) return
    inFlight.current = true
    const request = ++epoch.current
    setBusy('records'); setError('')
    try {
      let next = records
      const seen = new Set()
      while (next.hasMore) {
        if (!next.nextCursor || seen.has(next.nextCursor)) throw new Error('Invalid pagination')
        seen.add(next.nextCursor)
        const data = await call('timesheetReviewerEntries', { ...credentials.current, cursor: next.nextCursor })
        if (request !== epoch.current) return
        if (!Array.isArray(data?.entries)) throw new Error('Invalid page')
        next = { ...next, hasMore: data.hasMore, nextCursor: data.nextCursor, entries: mergeRows(next.entries, data.entries) }
      }
      setRecords(next)
    } catch (err) { if (request === epoch.current) failure(err, 'Could not complete the weekly records. Pay Week remains unavailable until all pages load.') }
    finally { if (request === epoch.current) { inFlight.current = false; setBusy('') } }
  }
  const correctTime = async (draft, password) => {
    if (inFlight.current || !auth.currentUser?.email) throw new Error('Sign in and wait for the current action to finish.')
    const user=auth.currentUser, request=++epoch.current
    inFlight.current=true;setBusy('correction')
    try {
      const {grantId}=await call('timesheetReviewerPrepareCorrection', draft)
      // Firebase auth_time has second precision. The confirmation must be newer
      // than both the existing authentication and this edit challenge.
      await new Promise(resolve=>setTimeout(resolve,1100))
      if(auth.currentUser?.uid!==user.uid) throw new Error('The signed-in account changed.')
      await reauthenticateWithCredential(user,EmailAuthProvider.credential(user.email,password))
      await user.getIdToken(true)
      if(request!==epoch.current || auth.currentUser?.uid!==user.uid) throw new Error('The account changed. Reopen this record.')
      const data=await call('timesheetReviewerCorrectTime',{grantId})
      if(request!==epoch.current) throw new Error('The account changed. Refresh to check the result.')
      setRecords(current=>({...current,serverNow:data.serverNow,entries:[data.entry,...current.entries.filter(e=>e.id!==data.entry.id)].sort((a,b)=>Date.parse(b.clockInAt)-Date.parse(a.clockInAt))}))
      setGallery(null)
    } catch(err) {
      if (err?.code?.startsWith('auth/')) throw new Error('Password could not be verified. Enter your own account password again.')
      throw err
    } finally {if(request===epoch.current){inFlight.current=false;setBusy('')}}
  }
  const payWeek = async payload => {
    if (inFlight.current || !credentials.current) throw new Error('Wait for the current action to finish.')
    inFlight.current = true; const request = ++epoch.current
    setBusy('payment'); setError('')
    try {
      const data = await call('timesheetReviewerPayWeek', { ...credentials.current, ...payload })
      if (request !== epoch.current) throw new Error('Dashboard locked. Reopen it to verify the payment.')
      if (!data?.payment?.id) throw new Error('Payment could not be verified.')
      setRecords(current => ({ ...current, payments: [...(current.payments || []).filter(p => p.id !== data.payment.id), data.payment] }))
    } catch (err) {
      if (request === epoch.current) failure(err, 'Could not archive the payment. Refresh to verify before retrying.')
      throw err
    } finally { if (request === epoch.current) { inFlight.current = false; setBusy('') } }
  }
  const acknowledge = async notificationId => {
    if (inFlight.current || !credentials.current) return
    inFlight.current = true
    const request = ++epoch.current
    setBusy(notificationId); setError('')
    try {
      const data = await call('timesheetReviewerAcknowledge', { ...credentials.current, notificationId })
      if (request !== epoch.current) return
      if (data?.ok !== true || !data.readAt) throw new Error('Invalid acknowledgement')
      setAlerts(current => ({ ...current, notifications: current.notifications.map(item => item.id === notificationId ? { ...item, readAt: data.readAt } : item) }))
    } catch (err) { if (request === epoch.current) failure(err, 'Could not mark this alert as read. Please try again.') }
    finally { if (request === epoch.current) { inFlight.current = false; setBusy('') } }
  }

  useEffect(() => { if (signedIn) load() }, [signedIn])

  if (records && alerts) return <TimesheetDashboard entries={records.entries} notifications={alerts.notifications}
    onCorrectTime={correctTime} payments={records.payments || []} onPayWeek={payWeek} roster={records.roster} onLoadAll={loadAll} reviewer={alerts.reviewer} loadedAt={records.serverNow} gallery={gallery} selectedEntryId={selectedEntryId} busy={busy} error={error}
    onSelectEntry={select} onLoadMorePhotos={() => { const entry = records.entries.find(item => item.id === selectedEntryId); if (entry) select(entry, true) }}
    onAcknowledge={acknowledge} onRefresh={load} onLock={clear}
    hasMoreEntries={records.hasMore} onMoreEntries={() => more('records')}
    hasMoreNotifications={alerts.hasMore} onMoreNotifications={() => more('alerts')} />

  return <section className="mx-auto my-8 max-w-xl rounded-xl border border-border bg-card p-8" aria-label="Attendance access">
    <h2 className="text-xl font-semibold">Team management</h2>
    <p className="mt-3" role="status">{busy ? 'Loading your dashboard…' : error || 'Sign in with your individual account to view this dashboard.'}</p>
    {signedIn && !busy && <Button className="mt-4" onClick={load}>Try again</Button>}
  </section>
}
